import { Popup } from "semantic-ui-react";
import cn from "classnames";

import styles from "./CardReporting.module.scss";
import { useDimensions } from "hooks/useDimensions";
import { MoboLink } from "components/MoboLink/MoboLink";
import { roundToTwoDigits } from "features/Library/Common/utils/roundToTwoDigits";
import { type ReactNode } from "react";

export type CardReportingItem = {
  stat: string | number;
  isStatPercentage?: boolean;
  popupHeader?: string;
  popupBody: string;
  width?: string;
  onClick?: () => void;
  url?: string;
} & ({ statDescription: string } | { statDescription: ReactNode; key: string });

export interface CardReportingProps {
  items: CardReportingItem[];
  /**
   * Number of columns to display cards in.
   * Limited to 1-6 to match the CSS classes generated in the SCSS file.
   */
  columnCount?: 1 | 2 | 3 | 4 | 5 | 6;
}

const CardReporting = ({ items, columnCount }: CardReportingProps) => {
  const results = items.map((card) => <Card card={card} key={"key" in card ? card.key : card.statDescription} />);

  const reportCardGroupClasses = cn(styles.reportCardGroup, columnCount ? styles[`cols-${columnCount}`] : null);

  return <div className={reportCardGroupClasses}>{results}</div>;
};

const Card = ({ card }: { card: CardReportingItem }) => {
  const [dimensions, measureRef] = useDimensions();

  const measuredWidth = dimensions?.width || "auto";

  const statValue = typeof card.stat === "string" ? card.stat : roundToTwoDigits(card.stat);

  const cardInfoBlock = () => (
    <>
      <div className={styles.popupInfo} />
      <p className={styles.stat}>
        {statValue}
        {card.isStatPercentage && "%"}
      </p>
      <p className={styles.statDescription}>{card.statDescription}</p>
    </>
  );
  return (
    <Popup
      key={"key" in card ? card.key : card.statDescription}
      trigger={
        <div
          ref={measureRef}
          onClick={card.onClick}
          className={cn(styles.reportCard, { [styles.onClick]: card.onClick || card.url })}
        >
          {card.url ? <MoboLink to={card.url}>{cardInfoBlock()}</MoboLink> : <>{cardInfoBlock()}</>}
        </div>
      }
      content={
        <div className={styles.tooltipMessage} style={{ width: card.width ?? measuredWidth }}>
          {card.popupHeader && <h2>{card.popupHeader}</h2>}
          {card.popupBody && <p>{card.popupBody}</p>}
        </div>
      }
      id={styles.popupDark}
      position="top center"
      disabled={!card.popupBody && !card.popupHeader}
      on={["hover", "focus"]}
    />
  );
};

export default CardReporting;
